// Task input modal for web workflow execution
// Uses TaskInputRenderer for input components

import { useState, useCallback, useMemo, useRef } from 'react';
import {
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  Icon,
  Box,
  useColorModeValue,
} from '@chakra-ui/react';
import { MdSend, MdCheckCircle, MdAdd, MdSync } from 'react-icons/md';
import DOMPurify from 'dompurify';
import { Modal } from '@/client/components/shared/Modal';
import type { WebTaskItem, MediaPayload } from '@/lib/sdk/workflows/types';
import { getChoiceOptionsWithIds, ChoiceOption } from '@/lib/sdk/chatbot/types';
import { TaskInputRenderer } from './TaskInputRenderer';
import { NoteHistory } from './NoteHistory';
import { TextInputTask } from './TextInputTask';
import { WhatsAppUploadTask } from './WhatsAppUploadTask';

interface TaskInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: WebTaskItem | null;
  onSubmitText: (value: string) => Promise<void>;
  onSubmitMedia: (media: MediaPayload) => Promise<void>;
  onSubmitChoice: (choiceId: string, choiceText: string) => Promise<void>;
  isLoading: boolean;
  botType?: string; // "static" for SOP, "dynamic" for FORM
  onRefresh?: () => Promise<void>; // Called after submit to refresh notes
  onConfirmWip?: (confirm: 'yes' | 'no') => Promise<void>;
  isConfirmingWip?: boolean;
  /** True if the task has WIP action configured — always show WIP-style UI */
  isWipConfigured?: boolean;
  /** Description text from task properties — may contain HTML */
  taskDescription?: string;
}

/**
 * Modal for entering task data based on task type
 * Uses TaskInputRenderer for consistent input rendering
 *
 * Layout:
 * - WIP / SOP add-more mode: 2-column grid (desktop) — left: inputs + buttons, right: WhatsApp upload
 * - All other modes: single-column layout with footer buttons
 */
export function TaskInputModal({
  isOpen,
  onClose,
  task,
  onSubmitText,
  onSubmitMedia,
  onSubmitChoice,
  isLoading,
  botType,
  onRefresh,
  onConfirmWip,
  isConfirmingWip = false,
  isWipConfigured = false,
  taskDescription,
}: TaskInputModalProps) {
  const [textValue, setTextValue] = useState('');
  const [selectedChoice, setSelectedChoice] = useState<ChoiceOption | null>(null);
  const [uploadedMedia, setUploadedMedia] = useState<MediaPayload | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [showWipConfirm, setShowWipConfirm] = useState(false);
  const previousTaskId = useRef<string | null>(null);

  const successBg = useColorModeValue('green.50', 'green.900');
  const wipBannerBg = useColorModeValue('orange.50', 'orange.900');
  const descriptionBg = useColorModeValue('gray.50', 'gray.800');

  // Check if SOP mode (allows multiple notes per task)
  const isSopMode = botType === 'static';

  // Reset state when task changes
  const currentTaskId = task?.task.id || null;
  if (currentTaskId !== previousTaskId.current) {
    previousTaskId.current = currentTaskId;
    if (task) {
      const shouldPrePopulate = !isSopMode && task.task.rep_status === 1;
      setTextValue(shouldPrePopulate ? (task.display_value || '') : '');
      setSelectedChoice(null);
      setUploadedMedia(null);
      setIsUploading(false);
      setError(null);
      setJustSubmitted(false);
      setShowWipConfirm(false);
    }
  }

  const taskType = task?.task.rep_type || '';
  const uiComponent = task?.ui_component || '';
  const isCompleted = task?.task.rep_status === 1;
  const isWip = task?.task.notes_enabled_mode === 'wip';
  const showWipUI = isWip || (isWipConfigured && !isCompleted);

  // "Add more" mode — drives 2-column layout and inline button placement
  const isAddMoreMode = showWipUI || (isSopMode && isCompleted);

  // Parse choices for choice task type
  const choices = useMemo((): ChoiceOption[] => {
    try {
      if (task?.task?.notes) {
        const choiceOptions = getChoiceOptionsWithIds(task.task.notes);
        if (choiceOptions.length > 0) return choiceOptions;
      }
      if (task?.validation?.accept) {
        return task.validation.accept.map((text, idx) => ({ id: `fallback_${idx}`, text }));
      }
      return [];
    } catch {
      return [];
    }
  }, [task?.task?.notes, task?.validation?.accept]);

  // Pre-populate selected choice for completed choice tasks
  const shouldPrePopulateChoice =
    uiComponent === 'choice_selector' &&
    isCompleted &&
    task?.display_value &&
    choices.length > 0 &&
    !selectedChoice;

  if (shouldPrePopulateChoice) {
    const matchingChoice = choices.find(c => c.text === task?.display_value);
    if (matchingChoice) setSelectedChoice(matchingChoice);
  }

  const resetForm = useCallback(() => {
    setTextValue('');
    setSelectedChoice(null);
    setUploadedMedia(null);
    setIsUploading(false);
    setError(null);
    setJustSubmitted(false);
    setShowWipConfirm(false);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleTextValue = useCallback((value: string) => {
    setTextValue(value);
    setError(null);
  }, []);

  const handleChoiceValue = useCallback((choice: ChoiceOption | null) => {
    setSelectedChoice(choice);
    setError(null);
  }, []);

  const handleMediaValue = useCallback((media: MediaPayload | null, uploading: boolean) => {
    setUploadedMedia(media);
    setIsUploading(uploading);
    setError(null);
  }, []);

  // Format date from YYYY-MM-DD to DDMMYYYY
  const formatDate = (dateStr: string): string => {
    if (!dateStr) return dateStr;
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    const [year, month, day] = parts;
    return `${day}${month}${year}`;
  };

  const handleSubmit = useCallback(async () => {
    if (!task) return;
    setError(null);

    try {
      // WIP mode note addition — always optional
      if (showWipUI) {
        if (uploadedMedia) {
          const mediaWithCaption = textValue ? { ...uploadedMedia, caption: textValue } : uploadedMedia;
          await onSubmitMedia(mediaWithCaption);
        } else if (textValue) {
          await onSubmitText(textValue);
        } else {
          setError('Please add a note or upload media');
          return;
        }
        resetForm();
        setJustSubmitted(true);
        if (onRefresh) await onRefresh();
        setTimeout(() => setJustSubmitted(false), 3000);
        return;
      }

      // SOP "Add More" mode
      if (isSopMode && isCompleted) {
        if (uploadedMedia) {
          const mediaWithCaption = textValue ? { ...uploadedMedia, caption: textValue } : uploadedMedia;
          await onSubmitMedia(mediaWithCaption);
        } else if (textValue) {
          await onSubmitText(textValue);
        } else {
          setError('Please add a note or upload media');
          return;
        }
      } else {
        // Normal mode - handle based on ui_component
        switch (uiComponent) {
          case 'choice_selector':
            if (!selectedChoice) { setError('Please select an option'); return; }
            await onSubmitChoice(selectedChoice.id, selectedChoice.text);
            break;

          case 'photo_upload':
          case 'video_upload':
          case 'audio_upload':
          case 'document_upload':
          case 'media_upload':
            if (uploadedMedia) {
              await onSubmitMedia(uploadedMedia);
            } else if (textValue) {
              await onSubmitText(textValue);
            } else {
              setError('Please upload a file or enter a note');
              return;
            }
            break;

          case 'date_picker':
            if (!textValue) { setError('Please select a date'); return; }
            await onSubmitText(formatDate(textValue));
            break;

          case 'number_input': {
            if (!textValue) { setError('Please enter a number'); return; }
            const num = parseFloat(textValue);
            if (isNaN(num)) { setError('Please enter a valid number'); return; }
            if (task.validation?.min !== undefined && num < task.validation.min) {
              setError(`Value must be at least ${task.validation.min}`); return;
            }
            if (task.validation?.max !== undefined && num > task.validation.max) {
              setError(`Value must be at most ${task.validation.max}`); return;
            }
            await onSubmitText(textValue);
            break;
          }

          case 'text_input':
          case 'optional_input':
          case 'location_picker':
          case 'address_input':
          case 'location_check':
          default:
            if (task.validation?.required && !textValue) {
              setError('This field is required'); return;
            }
            await onSubmitText(textValue);
            break;
        }
      }

      resetForm();

      if (isSopMode && isCompleted) {
        setJustSubmitted(true);
        if (onRefresh) await onRefresh();
        setTimeout(() => setJustSubmitted(false), 3000);
      } else {
        onClose();
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit';
      setError(errorMessage);
    }
  }, [
    task, uiComponent, textValue, uploadedMedia, selectedChoice,
    onSubmitChoice, onSubmitMedia, onSubmitText, onClose, resetForm,
    isSopMode, isCompleted, showWipUI, onRefresh,
  ]);

  if (!task) return null;

  // ── Buttons rendered inline in body for add-more mode ──────────────────────
  const renderAddMoreButtons = () => {
    if (showWipConfirm) {
      return (
        <VStack spacing={2} mt={3} align="stretch">
          <Text fontSize="sm" fontWeight="medium" color="orange.700" textAlign="center">
            Are you done with this task?
          </Text>
          <HStack spacing={2} justify="flex-end" flexWrap="wrap">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowWipConfirm(false)}
              isDisabled={isConfirmingWip}
            >
              Go Back
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={async () => { await onConfirmWip?.('no'); handleClose(); }}
              isLoading={isConfirmingWip}
            >
              Keep Working
            </Button>
            <Button
              size="sm"
              colorScheme="green"
              onClick={async () => { await onConfirmWip?.('yes'); handleClose(); }}
              isLoading={isConfirmingWip}
              loadingText="Saving..."
            >
              Mark Complete
            </Button>
          </HStack>
        </VStack>
      );
    }

    return (
      <HStack spacing={2} mt={3} justify="flex-end" flexWrap="wrap">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClose}
          isDisabled={isLoading || isUploading}
        >
          Cancel
        </Button>
        <Button
          colorScheme="brand"
          size="sm"
          leftIcon={<MdAdd />}
          onClick={handleSubmit}
          isLoading={isLoading}
          loadingText="Adding..."
          isDisabled={isUploading}
        >
          Add Note
        </Button>
        {showWipUI && (
          <Button
            colorScheme="orange"
            size="sm"
            leftIcon={<MdCheckCircle />}
            onClick={() => setShowWipConfirm(true)}
            isDisabled={isLoading || isUploading}
          >
            Mark as Done
          </Button>
        )}
      </HStack>
    );
  };

  // ── Footer for single-column (non-add-more) modes ──────────────────────────
  const renderFooter = () => (
    <HStack spacing={3} w="full" justify="flex-end">
      <Button variant="ghost" onClick={handleClose} isDisabled={isLoading || isUploading}>
        Cancel
      </Button>
      <Button
        colorScheme="brand"
        leftIcon={isCompleted && isSopMode ? <MdAdd /> : <MdSend />}
        onClick={handleSubmit}
        isLoading={isLoading}
        loadingText="Submitting..."
        isDisabled={isUploading}
      >
        {isCompleted && isSopMode ? 'Add More' : isCompleted ? 'Update' : 'Submit'}
      </Button>
    </HStack>
  );

  // ── Shared: badge + description + note history (used in both layouts) ──────
  const renderMetaSection = () => (
    <>
      <HStack flexWrap="wrap">
        <Badge colorScheme="purple" fontSize="xs">{taskType}</Badge>
        {task.validation?.required && (
          <Badge colorScheme="red" fontSize="xs">Required</Badge>
        )}
      </HStack>

      {taskDescription && (
        <Box
          p={3}
          bg={descriptionBg}
          borderRadius="md"
          borderWidth="1px"
          borderColor="border-default"
        >
          <Box
            fontSize="sm"
            color="text-primary"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(taskDescription) }}
            sx={{
              'ul, ol': { paddingLeft: '1.5em', marginBottom: '0.25em' },
              'li': { marginBottom: '0.15em' },
              'strong': { fontWeight: 'bold' },
              'em': { fontStyle: 'italic' },
              'p': { margin: 0, marginBottom: '0.3em' },
              'p:last-child': { marginBottom: 0 },
            }}
          />
        </Box>
      )}

      {/* Note history for WIP tasks */}
      {showWipUI && task.task.notes && task.task.notes.length > 0 && (
        <NoteHistory notes={task.task.notes} />
      )}

      {/* Note history for completed SOP tasks */}
      {isCompleted && isSopMode && task.task.notes && task.task.notes.length > 0 && (
        <NoteHistory notes={task.task.notes} />
      )}

      {/* Current value for FORM tasks */}
      {isCompleted && !isSopMode && task.display_value && (
        <Box>
          <Text fontSize="sm" fontWeight="medium" color="text-secondary">Current value:</Text>
          <Text fontSize="md" color="text-primary">{task.display_value}</Text>
        </Box>
      )}
    </>
  );

  // ── Add-more layout: stacked single column ────────────────────────────────
  const renderAddMoreLayout = () => (
    <VStack spacing={3} align="stretch">
      {renderMetaSection()}

      <TextInputTask
        description="Add a note"
        validation={{ ...(task.validation || { required: false }), required: false }}
        onSubmit={() => {}}
        controlled
        onValueChange={handleTextValue}
        initialValue={''}
      />

      {error && (
        <Text fontSize="sm" color="red.500">{error}</Text>
      )}

      {renderAddMoreButtons()}

      <WhatsAppUploadTask
        taskName={task.task.task_name}
        botCode={task.task.chatbot_code}
        taskId={task.task.task_id}
        repId={Number(task.task.id)}
        mediaType="media_upload"
      />
    </VStack>
  );

  // ── Standard single-column layout ─────────────────────────────────────────
  const renderStandardLayout = () => (
    <VStack spacing={4} align="stretch">
      {renderMetaSection()}

      <TaskInputRenderer
        task={task}
        onTextValue={handleTextValue}
        onMediaValue={handleMediaValue}
        onChoiceValue={handleChoiceValue}
        initialTextValue={isSopMode && isCompleted ? '' : (task.display_value || '')}
        initialChoice={selectedChoice || undefined}
        initialMedia={uploadedMedia || undefined}
        sopAddMoreMode={false}
      />

      {error && (
        <Text fontSize="sm" color="red.500">{error}</Text>
      )}
    </VStack>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={task.task.task_name}
      footer={isAddMoreMode ? undefined : renderFooter()}
      size="md"
    >
      <VStack spacing={4} align="stretch">
        {/* WIP status banner — always full width */}
        {showWipUI && (
          <Box p={3} bg={wipBannerBg} borderRadius="md">
            <HStack>
              <Icon as={MdSync} color="orange.500" />
              <Text fontSize="sm" color="orange.700">
                {isWip
                  ? 'This task is in progress. Add more notes or mark it as complete.'
                  : 'Add notes for this task, then mark it as complete when done.'}
              </Text>
            </HStack>
          </Box>
        )}

        {/* Status indicator for completed tasks — always full width */}
        {isCompleted && (
          <Box p={3} bg={justSubmitted ? 'green.100' : successBg} borderRadius="md">
            <HStack>
              <Icon as={MdCheckCircle} color="green.500" />
              <Text fontSize="sm" color="green.700">
                {justSubmitted
                  ? 'Response added successfully! Add another or close.'
                  : isSopMode
                    ? 'This task has responses. You can add more.'
                    : 'This task has been completed. You can update the value if needed.'}
              </Text>
            </HStack>
          </Box>
        )}

        {isAddMoreMode ? renderAddMoreLayout() : renderStandardLayout()}
      </VStack>
    </Modal>
  );
}